/**
 * FileDropZone — full-area drag-and-drop overlay for the chat surface.
 *
 * Lifted verbatim from `apps/agent/client/src/components/chat/file-drop-zone.tsx`
 * so the morph-bar's chat shell gets the same drop-anywhere → upload
 * behavior as agent.ordo.com. Once apps/agent migrates onto
 * @ordo/agent-embed (Phase 2 of the master plan), apps/agent imports
 * this same component instead of maintaining its own copy.
 *
 * Activates ONLY when the drag carries `Files` (so dragging text or
 * URLs from another part of the page doesn't trigger). Counter-based
 * enter/leave detection avoids flicker when the user drags across
 * nested children (every child fires its own dragleave event).
 */

import { Paperclip } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'

import { cn } from '../lib/cn'

interface FileDropZoneProps {
  onFiles: (files: File[]) => void
  className?: string
  children: React.ReactNode
  /** Optional override for the pill text. Default: "Drop to attach". */
  label?: string
  disabled?: boolean
}

const carriesFiles = (e: React.DragEvent): boolean => {
  return Array.from(e.dataTransfer.types ?? []).includes('Files')
}

export function FileDropZone({ onFiles, className, children, label = 'Drop to attach', disabled }: FileDropZoneProps) {
  const [active, setActive] = useState(false)
  const counter = useRef(0)

  const onDragEnter = useCallback(
    (e: React.DragEvent) => {
      if (disabled) {
        return
      }
      if (!carriesFiles(e)) {
        return
      }
      e.preventDefault()
      counter.current++
      if (counter.current === 1) {
        setActive(true)
      }
    },
    [disabled],
  )

  const onDragLeave = useCallback((e: React.DragEvent) => {
    if (!carriesFiles(e)) {
      return
    }
    counter.current = Math.max(0, counter.current - 1)
    if (counter.current === 0) {
      setActive(false)
    }
  }, [])

  const onDragOver = useCallback(
    (e: React.DragEvent) => {
      if (disabled) {
        return
      }
      if (!carriesFiles(e)) {
        return
      }
      e.preventDefault()
      e.dataTransfer.dropEffect = 'copy'
    },
    [disabled],
  )

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      if (disabled) {
        return
      }
      if (!carriesFiles(e)) {
        return
      }
      e.preventDefault()
      counter.current = 0
      setActive(false)
      const files = Array.from(e.dataTransfer.files ?? [])
      if (files.length > 0) {
        onFiles(files)
      }
    },
    [onFiles, disabled],
  )

  return (
    <div
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={cn('as-drop-host', className)}>
      {children}
      {active ? (
        <div className="as-drop-overlay">
          <div className="as-drop-pill">
            <Paperclip />
            {label}
          </div>
        </div>
      ) : null}
    </div>
  )
}
